import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook to track the active chapter in a scrollytelling experience.
 * Uses IntersectionObserver to determine which chapter is currently in viewport.
 *
 * @param {string[]} chapterIds - Array of chapter IDs in order
 * @returns {{ activeChapterId: string | null, chapterStates: Object<string, 'preview' | 'active' | 'past'> }}
 */
export function useActiveStoryChapter(chapterIds = []) {
  const [activeChapterId, setActiveChapterId] = useState(chapterIds[0] || null);
  const chapterRefsMap = useRef(new Map());
  const observerRef = useRef(null);

  // Memoize chapter states based on active chapter
  const chapterStates = useCallback(() => {
    if (!activeChapterId) return {};

    const activeIndex = chapterIds.indexOf(activeChapterId);
    return chapterIds.reduce((acc, id, index) => {
      if (index < activeIndex) {
        acc[id] = 'past';
      } else if (index === activeIndex) {
        acc[id] = 'active';
      } else {
        acc[id] = 'preview';
      }
      return acc;
    }, {});
  }, [activeChapterId, chapterIds]);

  // Register a chapter ref
  const registerChapterRef = useCallback((id, element) => {
    if (element) {
      chapterRefsMap.current.set(id, element);
    } else {
      chapterRefsMap.current.delete(id);
    }
  }, []);

  // Set up IntersectionObserver
  useEffect(() => {
    // Check for prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // If reduced motion is preferred, don't use scroll-based activation
    if (prefersReducedMotion) {
      return;
    }

    // Create observer with threshold to detect when chapter enters viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Find which chapter this element is
            const chapterId = Array.from(chapterRefsMap.current.entries()).find(
              ([, element]) => element === entry.target
            )?.[0];

            if (chapterId) {
              setActiveChapterId(chapterId);
            }
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of chapter is visible
        rootMargin: '0px 0px -30% 0px', // Adjust to top-heavy viewport
      }
    );

    observerRef.current = observer;

    // Observe all chapters
    chapterRefsMap.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    activeChapterId,
    chapterStates: chapterStates(),
    registerChapterRef,
  };
}
